import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../config/api";
import BillingNotificationsBell from "../../components/dashboard/BillingNotificationsBell";
import InactiveShipmentButton from "../../components/dashboard/InactiveShipmentButton";

const normalizeStatus = (value) => String(value || "").toLowerCase();

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DashboardCard = ({ title, value, subtitle, tone = "blue", icon, onClick }) => {
  const toneClasses = {
    blue: "border-blue-100 bg-blue-50 text-blue-700",
    green: "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    red: "border-red-100 bg-red-50 text-red-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };

  const body = (
    <>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${toneClasses[tone] || toneClasses.blue}`}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
        </div>
      </div>
      {subtitle ? <p className="mt-2 text-xs text-slate-500">{subtitle}</p> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
      >
        {body}
      </button>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      {body}
    </div>
  );
};

const SectionShell = ({ title, subtitle, action, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-sm">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-blue">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const RequestorDashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const userId = user?.user_id || user?.id || null;
  const [requests, setRequests] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [requestsRes, liquidationsRes] = await Promise.all([
          api.get("/api/accounting/requests"),
          api.get("/api/accounting/liquidations"),
        ]);

        setRequests(requestsRes.data?.data || []);
        setLiquidations(liquidationsRes.data?.data || []);
      } catch (error) {
        console.error("Failed to load requestor dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const myRequests = useMemo(
    () =>
      requests.filter((row) => {
        const preparerId = row.preparer_user_id || row.preparer_id || row.created_by_user_id;
        return String(preparerId || "") === String(userId || "");
      }),
    [requests, userId]
  );

  const myLiquidations = useMemo(
    () =>
      liquidations.filter((row) => {
        const preparerId = row.preparer_user_id || row.preparer_id || row.created_by_user_id;
        return String(preparerId || "") === String(userId || "");
      }),
    [liquidations, userId]
  );

  const stats = useMemo(() => {
    const total = myRequests.length;
    const drafts = myRequests.filter((row) => normalizeStatus(row.status) === "draft").length;
    const pending = myRequests.filter((row) => normalizeStatus(row.status) === "pending").length;
    const approved = myRequests.filter((row) => normalizeStatus(row.status) === "approved").length;
    const liquidationReady = myRequests.filter((row) => row.for_liquidation && normalizeStatus(row.status) === "approved").length;
    const liquidationReports = myLiquidations.filter((row) => row.liquidation_report).length;

    return { total, drafts, pending, approved, liquidationReady, liquidationReports };
  }, [myRequests, myLiquidations]);

  const recentRequests = useMemo(() => myRequests.slice(0, 6), [myRequests]);
  const recentLiquidations = useMemo(() => myLiquidations.slice(0, 6), [myLiquidations]);
  const summaryLine = `${stats.total} requests, ${stats.pending} pending, ${stats.approved} approved, and ${stats.liquidationReports} liquidation reports in your portal.`;

  const topCards = [
    { title: "Total Requests", value: stats.total, tone: "blue", icon: "fa-file-lines" },
    { title: "Approved Requests", value: stats.approved, tone: "green", icon: "fa-circle-check" },
    { title: "Pending Requests", value: stats.pending, tone: "amber", icon: "fa-hourglass-half" },
    { title: "For Liquidation", value: stats.liquidationReady, tone: "red", icon: "fa-receipt" },
    { title: "Draft Requests", value: stats.drafts, tone: "slate", icon: "fa-pen-to-square" },
    { title: "Liquidation Reports", value: stats.liquidationReports, tone: "green", icon: "fa-file-invoice-dollar" },
  ];

  const quickActions = [
    { title: "Request Creation", value: "Create a new request", to: "/requestor/request-create", icon: "fa-file-circle-plus" },
    { title: "Request Summary", value: "View your request list", to: "/requestor/request-summary", icon: "fa-table-list" },
    { title: "Liquidation Report", value: "Track liquidation progress", to: "/requestor/liquidation-report", icon: "fa-receipt" },
    { title: "JO Ledger", value: "Track job order activity", to: "/requestor/jo-ledger", icon: "fa-layer-group" },
  ];

  const attentionItems = [
    {
      title: "Requests waiting for approval",
      count: stats.pending,
      subtitle: "Requests still pending in the requestor flow",
      to: "/requestor/request-summary",
      tone: "amber",
    },
    {
      title: "Approved requests",
      count: stats.approved,
      subtitle: "Requests ready for liquidation tracking",
      to: "/requestor/liquidation-report",
      tone: "green",
    },
    {
      title: "Requests for liquidation",
      count: stats.liquidationReady,
      subtitle: "Approved requests marked for liquidation",
      to: "/requestor/liquidation-report",
      tone: "red",
    },
  ];

  const renderActivityRow = (row, title, meta, badge, onClick) => (
    <button
      key={row.id}
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-left transition hover:border-blue/30 hover:bg-blue-50"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-semibold text-slate-900">{title}</div>
          <div className="mt-1 text-sm text-slate-600">{meta}</div>
        </div>
        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600 ring-1 ring-slate-200">
          {badge}
        </span>
      </div>
    </button>
  );

  return (
    <div className="space-y-6 font-poppins">
      <header className="rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Requestor Portal</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-blue sm:text-4xl">
              Hello, {user?.first_name || user?.user_name || "Requestor"}
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{summaryLine}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <InactiveShipmentButton user={user} />
            <BillingNotificationsBell user={user} />
            {quickActions.map((action) => (
              <button
                key={action.title}
                type="button"
                onClick={() => navigate(action.to)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  action.title === "Request Creation"
                    ? "bg-blue text-white shadow-sm"
                    : action.title === "Request Summary"
                      ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      : action.title === "Liquidation Report"
                        ? "bg-yellow text-blue hover:shadow-sm"
                        : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                }`}
              >
                {action.title}
              </button>
            ))}
          </div>
        </div>
      </header>

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
          Loading requestor dashboard...
        </div>
      )}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-blue">Top KPIs</h2>
            <p className="text-sm text-slate-500">Core requestor activity in one view.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {topCards.map((card) => (
            <DashboardCard key={card.title} {...card} />
          ))}
        </div>
      </section>

      <SectionShell title="Workflow Progress" subtitle="Counts across your request creation flow.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                <i className="fa-solid fa-paper-plane" />
              </div>
              <span className="text-3xl font-semibold text-slate-900">{stats.pending}</span>
            </div>
            <div className="mt-4 font-semibold text-slate-900">Request Submitted</div>
            <div className="mt-1 text-sm text-slate-500">Requests still waiting for approval</div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <i className="fa-solid fa-circle-check" />
              </div>
              <span className="text-3xl font-semibold text-slate-900">{stats.approved}</span>
            </div>
            <div className="mt-4 font-semibold text-slate-900">Request Approved</div>
            <div className="mt-1 text-sm text-slate-500">Requests ready for liquidation tracking</div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                <i className="fa-solid fa-receipt" />
              </div>
              <span className="text-3xl font-semibold text-slate-900">{stats.liquidationReady}</span>
            </div>
            <div className="mt-4 font-semibold text-slate-900">For Liquidation</div>
            <div className="mt-1 text-sm text-slate-500">Approved requests marked for liquidation</div>
          </div>
        </div>
      </SectionShell>

      <SectionShell title="Quick Actions" subtitle="Direct links to the requestor screens.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {quickActions.map((action) => (
            <button
              key={action.title}
              type="button"
              onClick={() => navigate(action.to)}
              className="group rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue/30 hover:shadow-lg"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue/10 text-blue">
                    <i className={`fa-solid ${action.icon}`} />
                  </span>
                  <div>
                    <div className="font-semibold text-slate-900">{action.title}</div>
                    <div className="mt-1 text-sm text-slate-500">{action.value}</div>
                  </div>
                </div>
                <i className="fa-solid fa-arrow-right text-slate-300 transition group-hover:text-blue" />
              </div>
            </button>
          ))}
        </div>
      </SectionShell>

      <SectionShell title="Recent Activity" subtitle="Latest items from your requestor workflow.">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500">Latest Requests</div>
            {recentRequests.length ? (
              recentRequests.map((row) =>
                renderActivityRow(
                  row,
                  `${row.rfp_no || "—"} • ${row.job_order || "—"}`,
                  `${row.payee || "—"} • ${formatCurrency(row.total_amount)} • ${formatDateTime(row.created_at)}`,
                  normalizeStatus(row.status) || "request",
                  () => navigate("/requestor/request-summary")
                )
              )
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500">
                No requests found yet.
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500">Latest Liquidations</div>
            {recentLiquidations.length ? (
              recentLiquidations.map((row) =>
                renderActivityRow(
                  row,
                  `${row.rfp_no || "—"} • ${row.job_order || "—"}`,
                  `${row.payee || "—"} • ${row.liquidation_stage || "draft"} • ${formatDateTime(
                    row.liquidation_report?.report_date_created ||
                      row.liquidation_report?.created_at ||
                      row.updated_at ||
                      row.created_at
                  )}`,
                  row.liquidation_stage || "draft",
                  () => navigate("/requestor/liquidation-report")
                )
              )
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500">
                No liquidations found yet.
              </div>
            )}
          </div>
        </div>
      </SectionShell>

      <SectionShell title="Attention Panel" subtitle="Items that need your attention now.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {attentionItems.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={() => navigate(item.to)}
              className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-500">{item.title}</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-900">{item.count}</p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${
                    item.tone === "red"
                      ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                      : item.tone === "amber"
                        ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                        : "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                  }`}
                >
                  Alert
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-500">{item.subtitle}</p>
            </button>
          ))}
        </div>
      </SectionShell>
    </div>
  );
};

export default RequestorDashboard;
